import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const Process = () => {
  const canvasRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Animation du triangle - VITESSE x3 + LOOP INFINI
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let progress = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      ctx.scale(2, 2);
    };

    resize();

    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    const centerX = width / 2;
    const centerY = height / 2;
    const triangleSize = Math.min(width, height) * 0.35;

    const leftPoint = { x: centerX - triangleSize, y: centerY + triangleSize * 0.5 };
    const rightPoint = { x: centerX + triangleSize, y: centerY + triangleSize * 0.5 };
    const topPoint = { x: centerX, y: centerY - triangleSize * 0.8 };

    const drawGlow = (x, y, radius, color) => {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawLemniscate = (cx, cy, size, alpha) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#D4AF37';
      ctx.lineWidth = 3;
      ctx.shadowColor = '#D4AF37';
      ctx.shadowBlur = 25;
      
      ctx.beginPath();
      for (let t = 0; t <= Math.PI * 2; t += 0.01) {
        const scale = size;
        const x = cx + (scale * Math.cos(t)) / (1 + Math.sin(t) * Math.sin(t));
        const y = cy + (scale * Math.sin(t) * Math.cos(t)) / (1 + Math.sin(t) * Math.sin(t));
        if (t === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    };

    const drawLabel = (text, x, y, alpha, subtext) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#D4AF37';
      ctx.font = 'bold 14px Manrope';
      ctx.textAlign = 'center';
      ctx.fillText(text.toUpperCase(), x, y);
      if (subtext) {
        ctx.font = '11px Manrope';
        ctx.fillStyle = '#A8A29E';
        ctx.fillText(subtext, x, y + 16);
      }
      ctx.restore();
    };

    const animate = () => {
      if (!isInView) {
        animationId = requestAnimationFrame(animate);
        return;
      }

      // VITESSE x3
      progress += 0.012;
      ctx.clearRect(0, 0, width, height);

      // Phases de l'animation
      const phase1 = Math.min(progress * 3, 1); // Point gauche
      const phase2 = Math.max(0, Math.min((progress - 0.33) * 3, 1)); // Ligne base
      const phase3 = Math.max(0, Math.min((progress - 0.66) * 3, 1)); // Montee sommet
      const phase4 = Math.max(0, Math.min((progress - 1) * 3, 1)); // Lemniscate

      ctx.lineCap = 'round';
      ctx.lineWidth = 3;

      // Phase 1: Point gauche (Raisonnement)
      if (phase1 > 0) {
        drawGlow(leftPoint.x, leftPoint.y, 25 * phase1, 'rgba(212, 175, 55, 0.3)');
        
        ctx.beginPath();
        ctx.arc(leftPoint.x, leftPoint.y, 6 * phase1, 0, Math.PI * 2);
        ctx.fillStyle = '#D4AF37';
        ctx.shadowColor = '#D4AF37';
        ctx.shadowBlur = 15;
        ctx.fill();
        ctx.shadowBlur = 0;

        drawLabel('Raisonnement', leftPoint.x, leftPoint.y + 35, phase1, 'Comprendre');
      }

      // Phase 2: Ligne base vers droite (Resonance)
      if (phase2 > 0) {
        const currentX = leftPoint.x + (rightPoint.x - leftPoint.x) * phase2;

        ctx.beginPath();
        ctx.moveTo(leftPoint.x, leftPoint.y);
        ctx.lineTo(currentX, leftPoint.y);
        ctx.strokeStyle = '#D4AF37';
        ctx.shadowColor = '#D4AF37';
        ctx.shadowBlur = 10;
        ctx.stroke();
        ctx.shadowBlur = 0;

        if (phase2 >= 1) {
          drawGlow(rightPoint.x, rightPoint.y, 25, 'rgba(212, 175, 55, 0.3)');
          
          ctx.beginPath();
          ctx.arc(rightPoint.x, rightPoint.y, 6, 0, Math.PI * 2);
          ctx.fillStyle = '#D4AF37';
          ctx.shadowColor = '#D4AF37';
          ctx.shadowBlur = 15;
          ctx.fill();
          ctx.shadowBlur = 0;

          drawLabel('Résonance', rightPoint.x, rightPoint.y + 35, 1, 'Ressentir');
        }
      }

      // Phase 3: Lignes montent vers sommet (Rayonnement)
      if (phase3 > 0) {
        const leftToTopX = leftPoint.x + (topPoint.x - leftPoint.x) * phase3;
        const leftToTopY = leftPoint.y + (topPoint.y - leftPoint.y) * phase3;
        const rightToTopX = rightPoint.x + (topPoint.x - rightPoint.x) * phase3;
        const rightToTopY = rightPoint.y + (topPoint.y - rightPoint.y) * phase3;

        ctx.beginPath();
        ctx.moveTo(leftPoint.x, leftPoint.y);
        ctx.lineTo(leftToTopX, leftToTopY);
        ctx.strokeStyle = '#D4AF37';
        ctx.shadowColor = '#D4AF37';
        ctx.shadowBlur = 10;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(rightPoint.x, rightPoint.y);
        ctx.lineTo(rightToTopX, rightToTopY);
        ctx.stroke();
        ctx.shadowBlur = 0;

        if (phase3 >= 1) {
          drawGlow(topPoint.x, topPoint.y, 35, 'rgba(212, 175, 55, 0.4)');
          
          ctx.beginPath();
          ctx.arc(topPoint.x, topPoint.y, 8, 0, Math.PI * 2);
          ctx.fillStyle = '#D4AF37';
          ctx.shadowColor = '#D4AF37';
          ctx.shadowBlur = 20;
          ctx.fill();
          ctx.shadowBlur = 0;

          drawLabel('Rayonnement', topPoint.x, topPoint.y - 25, 1, 'Déployer');
        }
      }

      // Phase 4: Lemniscate au centre
      if (phase4 > 0) {
        const triangleCenterX = (leftPoint.x + rightPoint.x + topPoint.x) / 3;
        const triangleCenterY = (leftPoint.y + rightPoint.y + topPoint.y) / 3;

        drawGlow(triangleCenterX, triangleCenterY, 50 * phase4, 'rgba(212, 175, 55, 0.3)');
        drawGlow(triangleCenterX, triangleCenterY, 25 * phase4, 'rgba(243, 229, 171, 0.5)');

        drawLemniscate(triangleCenterX, triangleCenterY, 30 * phase4, phase4);
      }

      // LOOP INFINI - Reset apres completion
      if (progress > 1.8) {
        progress = 0;
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isInView]);

  return (
    <section
      ref={sectionRef}
      id="process"
      data-testid="process-section"
      className="relative py-24 md:py-32 bg-[#0C0A09] overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917] via-[#0C0A09] to-[#1C1917]" />
      <div className="absolute inset-0 noise-overlay" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-4">
            Notre Processus
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#FAFAF9] leading-tight">
            Développer son empreinte en{' '}
            <span className="italic text-[#D4AF37]">3 dimensions</span>
          </h2>
          <p className="mt-6 text-[#A8A29E] text-lg max-w-2xl mx-auto">
            Nous vous accompagnons à déconstruire vos programmes pour incarner votre
            signature, bâtir votre temple et rayonner.
          </p>
        </motion.div>

        {/* Animation Video Style Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-[#0C0A09] rounded-2xl border border-[#D4AF37]/20 overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <canvas
                  ref={canvasRef}
                  className="w-full h-full"
                  data-testid="triangle-animation"
                />
              </div>
              <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-[#D4AF37]/30" />
              <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-[#D4AF37]/30" />
              <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-[#D4AF37]/30" />
              <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#D4AF37]/30" />
            </div>
          </div>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              number: '01',
              title: 'Raisonnement',
              description: 'Visiter avec conscience et analyse certains domaines de notre vie. Les blocages ne s\'harmonisent que lorsqu\'ils sont vus et reconnus.',
              icon: 'https://devgilles.wptotem.com/wp-content/uploads/2024/03/gilles1385_full_brain_cerveau_synapses_neurones_lumiere_eveil_f_463c094e-7c71-416e-967f-23e686570fe1-150x150.png',
            },
            {
              number: '02',
              title: 'Résonance',
              description: 'Intégrer et ressentir jusque dans la dimension cellulaire les espaces de résolution et de résilience que nous nous offrons.',
              icon: 'https://devgilles.wptotem.com/wp-content/uploads/2024/03/gilles1385_ADN_atoms_energy_cosmos_univers_electric_arcs_light__7cc87e15-878c-48da-938c-b03d608dd71d-150x150.png',
            },
            {
              number: '03',
              title: 'Rayonnement',
              description: 'Déployer nos deux ailes pour retrouver une envergure naturelle, reprendre notre trône et œuvrer en souveraineté.',
              icon: 'https://devgilles.wptotem.com/wp-content/uploads/2023/10/gilles1385_symbole_3d_geometry_sacree_pour_decrire_le_mot_AMEN__038ebd6a-7b68-4887-8fda-a3ecae2d5c8d-150x150.png',
            },
          ].map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="group relative"
              data-testid={`process-card-${index}`}
            >
              <div className="glass-card rounded-2xl p-8 h-full hover:border-[#D4AF37]/50 transition-all duration-300">
                <span className="absolute -top-4 -left-2 font-display text-6xl text-[#D4AF37]/20 font-bold">
                  {step.number}
                </span>
                <div className="mb-6">
                  <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center overflow-hidden">
                    <img src={step.icon} alt={step.title} className="w-16 h-16 object-cover rounded-full" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-[#FAFAF9] mb-4">{step.title}</h3>
                <p className="text-[#A8A29E] leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
